import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_DELIVERY_STATUSES,
  NOTIFICATION_EVENT_TYPES,
  type AdminNotificationControl,
  type AdminNotificationMetrics,
} from '@steado/admin-contract/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { Panel, StatCards } from '@/components/panel/panels';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { TONES } from '@/components/table/tone';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, humanise } from '@/lib/format';
import { cn } from '@/lib/utils';

import { NotificationDeliveryDrawer } from './NotificationDeliveryDrawer';
import {
  DELIVERY_STATUS_LABELS,
  DELIVERY_STATUS_TONES,
  EVENT_TYPE_LABELS,
  PLATFORM_LABELS,
  eventTypeLabel,
} from './notification-labels';
import {
  DELIVERIES_PAGE_SIZE,
  notificationControlsQueryOptions,
  notificationDeliveriesQueryOptions,
  notificationMetricsQueryOptions,
  setNotificationControl,
} from './notifications-query';
import type { NotificationWindow, NotificationsSearch } from './notifications-search';

/** The three service levels, in the order an operator reads them. */
const SLOS = [
  {
    key: 'transactionalOutboxLag',
    name: 'Transactional outbox lag',
    unit: 's',
  },
  { key: 'reminderSchedulerLag', name: 'Reminder scheduler lag', unit: 's' },
  { key: 'providerAcceptance', name: 'Provider acceptance', unit: '%' },
] as const;

const WINDOW_LABELS: Readonly<Record<NotificationWindow, string>> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
};

const COLUMNS: readonly TableColumn[] = [
  { key: 'what', label: 'What', width: '1.3fr' },
  { key: 'who', label: 'Sent to', width: '1.1fr' },
  { key: 'status', label: 'How it ended', width: '1.3fr' },
  { key: 'device', label: 'Device', width: '82px' },
  { key: 'due', label: 'Due', width: '130px' },
  { key: 'sent', label: 'Sent', width: '130px' },
  { key: 'tries', label: 'Tries', width: '64px', align: 'right' },
];

const BY_TYPE_COLUMNS = ['Sent', 'Held back', 'Failed', 'Waiting'] as const;

const TIMING_LABELS = { past: 'Sent & past', upcoming: 'Upcoming' } as const;

/**
 * Every push the system tried to send, with the counts that summarise them.
 *
 * The list comes first because "what did we send, to whom, and did it land"
 * is the question this screen is opened with; the pipeline's service levels
 * and kill switches are for the rarer day something is wrong, so they sit
 * underneath. Rows carry the event type and outcome, never the message text —
 * the delivery routes exclude copy on purpose (`docs/NOTIFICATIONS.md`).
 */
export function NotificationsScreen({
  search,
  onSearchChange,
}: {
  readonly search: NotificationsSearch;
  readonly onSearchChange: (next: NotificationsSearch) => void;
}) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const metricsWindow = search.window ?? '24h';
  const metrics = useQuery(notificationMetricsQueryOptions(api, metricsWindow));
  const controls = useQuery(notificationControlsQueryOptions(api));
  const deliveries = useQuery(notificationDeliveriesQueryOptions(api, search));
  const [pendingControl, setPendingControl] = useState<AdminNotificationControl | null>(null);
  /**
   * The API hands back only a forward cursor, so walking back needs the
   * cursors already visited — the same arrangement as the Users screen.
   */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const controlMutation = useMutation({
    mutationFn: (control: AdminNotificationControl) => setNotificationControl(api, control, !control.enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'notification-controls'] });
      setPendingControl(null);
    },
    retry: false,
  });

  const setFilter = useCallback(
    (patch: Partial<NotificationsSearch>) => {
      // Any filter change invalidates the paging position.
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        value: search.status,
        options: NOTIFICATION_DELIVERY_STATUSES.map((status) => ({
          value: status,
          label: DELIVERY_STATUS_LABELS[status],
        })),
        onChange: (status) => {
          setFilter({ status: status as NotificationsSearch['status'] });
        },
      },
      {
        id: 'eventType',
        label: 'Type',
        value: search.eventType,
        options: NOTIFICATION_EVENT_TYPES.map((eventType) => ({
          value: eventType,
          label: EVENT_TYPE_LABELS[eventType],
        })),
        onChange: (eventType) => {
          setFilter({ eventType: eventType as NotificationsSearch['eventType'] });
        },
      },
      {
        id: 'category',
        label: 'Category',
        value: search.category,
        options: NOTIFICATION_CATEGORIES.map((category) => ({
          value: category,
          label: humanise(category.toLowerCase()),
        })),
        onChange: (category) => {
          setFilter({ category: category as NotificationsSearch['category'] });
        },
      },
    ],
    [search.status, search.eventType, search.category, setFilter],
  );

  const isFiltered =
    search.search !== undefined ||
    search.status !== undefined ||
    search.eventType !== undefined ||
    search.category !== undefined;

  const onReset = () => {
    setCursorStack([]);
    onSearchChange({ window: search.window, when: search.when });
  };

  const timing = search.when ?? 'past';

  const rows: readonly TableRow[] = (deliveries.data?.items ?? []).map((delivery) => ({
    id: delivery.id,
    onOpen: () => {
      onSearchChange({ ...search, delivery: delivery.id });
    },
    cells: [
      <TextCell
        key="what"
        value={eventTypeLabel(delivery.eventType)}
        sub={humanise(delivery.category.toLowerCase())}
        weight="medium"
      />,
      <TextCell key="who" value={delivery.displayName ?? 'Unnamed'} sub={delivery.phone} />,
      <span key="status" className="flex min-w-0 flex-col gap-1">
        <BadgeCell
          label={DELIVERY_STATUS_LABELS[delivery.status]}
          tone={DELIVERY_STATUS_TONES[delivery.status]}
        />
        {delivery.suppressionReason !== null ? (
          <span className="truncate text-xs text-muted-foreground">
            {humanise(delivery.suppressionReason.toLowerCase())}
          </span>
        ) : delivery.lastErrorCode !== null && delivery.status !== 'SENT' ? (
          <span className="truncate font-mono text-xs text-muted-foreground">{delivery.lastErrorCode}</span>
        ) : null}
      </span>,
      <TextCell key="device" value={PLATFORM_LABELS[delivery.platform]} />,
      <TextCell key="due" value={formatDateTime(delivery.dueAt)} />,
      <TextCell key="sent" value={delivery.sentAt === null ? null : formatDateTime(delivery.sentAt)} />,
      <TextCell key="tries" value={String(delivery.attemptCount)} mono align="right" />,
    ],
  }));

  const data = metrics.data;
  const outcomes = outcomeTotals(data);
  const pageOffset = cursorStack.length * DELIVERIES_PAGE_SIZE;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      <ScopePills
        label="Metrics window"
        scopes={(Object.keys(WINDOW_LABELS) as NotificationWindow[]).map((id) => ({
          id,
          label: WINDOW_LABELS[id],
        }))}
        value={metricsWindow}
        onChange={(next) => {
          onSearchChange({ ...search, window: next === '24h' ? undefined : next });
        }}
      />

      {metrics.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{metrics.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <StatCards
        stats={[
          {
            label: 'Sent',
            value: count(outcomes?.sent),
            note: 'Accepted by the push provider for delivery to the device.',
          },
          {
            label: 'Held back',
            value: count(outcomes?.suppressed),
            note: 'Skipped on purpose: quiet hours, preferences, caps, or a plan that moved on.',
          },
          {
            label: 'Failed',
            value: count(outcomes?.failed),
            note: 'The provider refused it, or it ran out of retries.',
          },
          {
            label: 'Waiting',
            value: count(outcomes?.pending),
            note: 'Scheduled, deferred, or being sent right now.',
          },
        ]}
      />

      <ScopePills
        label="Delivery timing"
        className="mt-3"
        scopes={(['past', 'upcoming'] as const).map((id) => ({ id, label: TIMING_LABELS[id] }))}
        value={timing}
        onChange={(next) => {
          setFilter({ when: next === 'past' ? undefined : next });
        }}
      />

      <TableToolbar
        searchValue={search.search ?? ''}
        searchPlaceholder="Name, phone, or a user or delivery ID"
        onSearchChange={(value) => {
          setFilter({ search: value.trim() === '' ? undefined : value.trim() });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      {deliveries.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{deliveries.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        caption="Notification deliveries"
        title={timing === 'past' ? 'Sent and past deliveries, newest first' : 'Upcoming, soonest first'}
        columns={COLUMNS}
        rows={rows}
        isLoading={deliveries.isPending}
        emptyTitle={isFiltered ? 'No matches' : timing === 'past' ? 'Nothing sent yet' : 'Nothing scheduled'}
        emptyBody={
          isFiltered
            ? 'No delivery matches the current search and filters.'
            : timing === 'past'
              ? 'Deliveries appear here once they are due, whether they were sent, held back or failed.'
              : 'Nothing is waiting to go out. Reminders are scheduled a few days ahead for people with the app installed.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={deliveries.data?.pagination.hasMore ?? false}
            onPrevious={() => {
              setCursorStack((stack) => {
                onSearchChange({ ...search, cursor: stack[stack.length - 1] });
                return stack.slice(0, -1);
              });
            }}
            onNext={() => {
              const next = deliveries.data?.pagination.nextCursor;
              if (next === undefined || next === null) return;
              setCursorStack((stack) => [...stack, search.cursor]);
              onSearchChange({ ...search, cursor: next });
            }}
            isLoading={deliveries.isPending}
          />
        }
      />

      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.35fr_1fr]">
        <Panel
          title="By type"
          description={`How each kind of notification ended, over the ${WINDOW_LABELS[metricsWindow].toLowerCase()}.`}
          bleed
        >
          {(data?.eventTypes ?? []).length === 0 ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[12.5px] text-muted-foreground">
              {data === undefined ? 'Reading…' : 'Nothing was scheduled or sent in this window.'}
            </p>
          ) : (
            <table className="w-full border-t border-border text-[13px]">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th scope="col" className="px-[18px] py-2 text-left font-medium">
                    Type
                  </th>
                  {BY_TYPE_COLUMNS.map((label) => (
                    <th key={label} scope="col" className="px-3 py-2 text-right font-medium last:pr-[18px]">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.eventTypes ?? []).map((row) => (
                  <tr key={row.eventType} className="border-t border-border">
                    <th scope="row" className="px-[18px] py-2 text-left font-normal">
                      <button
                        type="button"
                        className="text-foreground hover:underline"
                        title="Show these in the list"
                        onClick={() => {
                          setFilter({ eventType: row.eventType as NotificationsSearch['eventType'] });
                        }}
                      >
                        {eventTypeLabel(row.eventType)}
                      </button>
                    </th>
                    {[row.sent, row.suppressed, row.failed, row.pending].map((value, index) => (
                      <td
                        key={BY_TYPE_COLUMNS[index]}
                        className={cn(
                          'px-3 py-2 text-right font-mono last:pr-[18px]',
                          value === 0
                            ? 'text-muted-foreground/60'
                            : index === 2
                              ? 'text-err-fg'
                              : 'text-foreground',
                        )}
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Why sends were held back" description="Busiest reasons first." bleed>
          {(data?.suppressions ?? []).length === 0 ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[12.5px] text-muted-foreground">
              {data === undefined ? 'Reading…' : 'Nothing was held back in this window.'}
            </p>
          ) : (
            <ul>
              {[...(data?.suppressions ?? [])]
                .sort((left, right) => right.count - left.count)
                .map((suppression) => (
                  <li
                    key={suppression.reason}
                    className="flex items-baseline gap-3 border-t border-border px-[18px] py-2"
                  >
                    <span className="text-[13px] text-foreground">
                      {humanise(suppression.reason.toLowerCase())}
                    </span>
                    <span className="ml-auto font-mono text-[13px] text-muted-foreground">
                      {suppression.count}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </Panel>
      </div>

      <h2 className="mt-4 text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
        Pipeline health
      </h2>

      <StatCards
        stats={[
          {
            label: 'Due backlog',
            value: count(data?.backlog.dueDeliveryCount),
            note: 'Deliveries past their due time and not yet sent.',
          },
          {
            label: 'Oldest due',
            value: age(data?.backlog.oldestDueDeliveryAgeSeconds),
            note: 'How long the most patient one has been waiting.',
          },
          {
            label: 'Urgent backlog',
            value: count(data?.backlog.p0p1DueDeliveryCount),
            note: `P0 and P1 only. Oldest ${age(data?.backlog.oldestP0P1DueDeliveryAgeSeconds)}.`,
          },
          {
            label: 'Outbox waiting',
            value: count(data?.backlog.outboxCount),
            note: `Events not yet turned into deliveries. Oldest ${age(
              data?.backlog.oldestOutboxAgeSeconds,
            )}.`,
          },
        ]}
      />

      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
        <div className="flex flex-col gap-3">
          <Panel
            title="Service levels"
            description={`Each measured against its own target, over the ${WINDOW_LABELS[metricsWindow].toLowerCase()}.`}
            bleed
          >
            <ul>
              {SLOS.map((slo) => {
                const measurement = data?.slo[slo.key];
                return (
                  <li
                    key={slo.key}
                    className="flex items-center gap-3 border-t border-border px-[18px] py-[11px]"
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[13px] font-medium text-foreground">{slo.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {measurement === undefined
                          ? 'no reading yet'
                          : `${measurement.comparison === 'LTE' ? 'at most' : 'at least'} ${String(
                              measurement.target,
                            )}${slo.unit} · ${String(measurement.sampleSize)} sampled`}
                      </span>
                    </span>
                    <span className="ml-auto flex shrink-0 items-center gap-2">
                      <span className="font-mono text-sm font-medium text-foreground">
                        {measurement?.value == null ? '—' : `${measurement.value.toFixed(1)}${slo.unit}`}
                      </span>
                      <span
                        className={cn(
                          'inline-block size-[7px] rounded-full',
                          // Null is "nothing was sampled", which is neither
                          // healthy nor broken and must not be painted green.
                          measurement?.healthy == null
                            ? 'bg-mute-fg'
                            : measurement.healthy
                              ? 'bg-ok-fg'
                              : 'bg-err-fg',
                        )}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          </Panel>

          <Panel title="Provider errors" bleed>
            {providerErrors(data).length === 0 ? (
              <p className="border-t border-border px-[18px] py-3.5 text-[12.5px] text-muted-foreground">
                {data === undefined ? 'Reading…' : 'No attempt failed in this window.'}
              </p>
            ) : (
              <ul>
                {providerErrors(data).map((error) => (
                  <li
                    key={error.code}
                    className="flex items-baseline gap-2.5 border-t border-border px-[18px] py-2"
                  >
                    <span
                      className={cn(
                        'font-mono text-[12.5px]',
                        error.isTerminal ? 'text-err-fg' : 'text-warn-fg',
                      )}
                    >
                      {error.code}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {error.isTerminal ? 'terminal' : 'retryable'}
                    </span>
                    <span className="ml-auto font-mono text-[13px] font-medium text-foreground">
                      {error.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <Panel
          title="Kill switches"
          description="A switch turned off in the environment stays off; nothing here can override it."
          bleed
        >
          {controls.isError ? (
            <div className="border-t border-border px-[18px] py-3.5">
              <Alert role="alert" tone="err">
                <AlertDescription>{controls.error.message}</AlertDescription>
              </Alert>
            </div>
          ) : null}
          <ul>
            {(controls.data ?? []).map((control) => (
              <li
                key={control.key}
                className="flex items-center gap-3 border-t border-border px-[18px] py-2.5"
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-mono text-[12.5px] text-foreground">{control.key}</span>
                  <span className="text-xs text-pretty text-muted-foreground">
                    {control.reasonCode === null
                      ? control.effectiveEnabled
                        ? 'Sending normally.'
                        : 'Held by an operator.'
                      : humanise(control.reasonCode)}
                  </span>
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-2">
                  <span
                    className={cn(
                      'inline-flex h-5 items-center rounded-[10px] px-2 text-xs font-semibold',
                      TONES[control.effectiveEnabled ? 'ok' : 'err'],
                    )}
                  >
                    {control.effectiveEnabled ? 'on' : control.enabled ? 'env blocked' : 'off'}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={!can.mutate}
                    title={can.mutate ? undefined : 'Requires the admin role.'}
                    onClick={() => {
                      setPendingControl(control);
                    }}
                  >
                    {control.enabled ? 'Disable…' : 'Enable…'}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {search.delivery === undefined ? null : (
        <NotificationDeliveryDrawer
          deliveryId={search.delivery}
          onClose={() => {
            onSearchChange({ ...search, delivery: undefined });
          }}
        />
      )}

      {pendingControl === null ? null : (
        <ConfirmDialog
          title={`${pendingControl.enabled ? 'Disable' : 'Enable'} ${pendingControl.key}?`}
          body="This takes effect immediately, survives a restart, and is recorded against your account. A switch turned off in the environment stays off regardless."
          confirmLabel={pendingControl.enabled ? 'Disable' : 'Enable'}
          destructive={pendingControl.enabled}
          typeToConfirm={pendingControl.key}
          isPending={controlMutation.isPending}
          {...(controlMutation.isError ? { error: controlMutation.error.message } : {})}
          onConfirm={() => {
            controlMutation.mutate(pendingControl);
          }}
          onCancel={() => {
            setPendingControl(null);
          }}
        />
      )}
    </div>
  );
}

/** The four headline outcomes, folded from the seven delivery statuses. */
function outcomeTotals(metrics: AdminNotificationMetrics | undefined) {
  if (metrics === undefined) return undefined;
  const of = (...statuses: string[]) =>
    metrics.deliveryOutcomes
      .filter((outcome) => statuses.includes(outcome.status))
      .reduce((sum, outcome) => sum + outcome.count, 0);
  return {
    sent: of('SENT'),
    suppressed: of('SUPPRESSED'),
    failed: of('FAILED_TERMINAL', 'FAILED_RETRYABLE'),
    pending: of('SCHEDULED', 'PROCESSING', 'DEFERRED'),
  };
}

/**
 * The provider's own refusals, rolled up by code.
 *
 * `attemptOutcomes` is keyed on (status, errorClass, errorCode) so a code can
 * appear twice; this sums them and keeps the worse class, because "invalid
 * token, 12 of them" is the fact and which attempt number they were is not.
 */
function providerErrors(
  metrics: AdminNotificationMetrics | undefined,
): readonly { readonly code: string; readonly isTerminal: boolean; readonly count: number }[] {
  const byCode = new Map<string, { code: string; isTerminal: boolean; count: number }>();

  for (const outcome of metrics?.attemptOutcomes ?? []) {
    if (outcome.errorCode === null) continue;
    const existing = byCode.get(outcome.errorCode);
    if (existing === undefined) {
      byCode.set(outcome.errorCode, {
        code: outcome.errorCode,
        isTerminal: outcome.errorClass === 'TERMINAL',
        count: outcome.count,
      });
      continue;
    }
    existing.count += outcome.count;
    existing.isTerminal = existing.isTerminal || outcome.errorClass === 'TERMINAL';
  }

  return [...byCode.values()].sort((left, right) => right.count - left.count);
}

function count(value: number | undefined): string {
  return value === undefined ? '—' : String(value);
}

function age(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  return seconds < 60 ? `${String(Math.round(seconds))}s` : `${String(Math.floor(seconds / 60))}m`;
}
